import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from './utils.js';
export function useModalBehavior(open, onClose, contentRef) {
    useEffect(() => {
        if (!open)
            return;
        const previousFocus = document.activeElement;
        const handleKey = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
            if (e.key === 'Tab' && contentRef.current) {
                const focusable = contentRef.current.querySelectorAll('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
                if (focusable.length === 0)
                    return;
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
                else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener('keydown', handleKey);
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const focusTimer = window.setTimeout(() => {
            const autofocus = contentRef.current?.querySelector('[data-autofocus]');
            if (autofocus) {
                autofocus.focus();
            }
            else {
                const firstFocusable = contentRef.current?.querySelector('button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled])');
                firstFocusable?.focus();
            }
        }, 10);
        return () => {
            document.removeEventListener('keydown', handleKey);
            document.body.style.overflow = originalOverflow;
            window.clearTimeout(focusTimer);
            previousFocus?.focus?.();
        };
    }, [open, onClose, contentRef]);
}
const SIZE_CLASSES = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
};
export function Dialog({ open, onClose, children, size = 'md', closeOnBackdropClick = true, 'aria-label': ariaLabel, }) {
    const contentRef = useRef(null);
    useModalBehavior(open, onClose, contentRef);
    if (!open)
        return null;
    if (typeof document === 'undefined')
        return null;
    return createPortal(_jsxs("div", { className: "responsive-dialog fixed inset-0 flex items-center justify-center p-4", 
        // Above the app's z-index:100 custom overlays (invite/add-store/new-plan
        // drawer …) so a confirm spawned from one of them isn't occluded.
        style: { zIndex: 120 }, role: "dialog", "aria-modal": "true", "aria-label": ariaLabel, children: [_jsx("div", { className: "absolute inset-0 bg-black/60 backdrop-blur-sm", onClick: closeOnBackdropClick ? onClose : undefined, "aria-hidden": "true" }), _jsx("div", { ref: contentRef, className: cn(
                // Cap the panel to the viewport and lay it out as a column so the
                // header/footer stay pinned and DialogBody takes the leftover space.
                // Without this a long body grows past the screen and pushes the
                // footer's actions out of reach — the page can't scroll either,
                // because the modal locks body overflow while it's open.
                'relative z-10 flex max-h-[85vh] w-full flex-col overflow-hidden rounded-lg border border-border bg-card text-card-foreground shadow-2xl', SIZE_CLASSES[size]), children: children })] }), document.body);
}
export function DialogHeader({ title, description, onClose }) {
    return (_jsxs("div", { className: "flex shrink-0 items-start justify-between border-b border-border px-5 py-4", children: [_jsxs("div", { children: [_jsx("h2", { className: "text-base font-semibold text-card-foreground", children: title }), description && _jsx("p", { className: "mt-0.5 text-xs text-muted", children: description })] }), onClose && (_jsx("button", { type: "button", onClick: onClose, className: "rounded-md p-1 text-muted transition hover:bg-accent hover:text-card-foreground", "aria-label": "Close dialog", children: _jsx(X, { className: "h-4 w-4" }) }))] }));
}
/** The dialog's scrollable region — grows to fill the panel, scrolls when the content is tall. */
export function DialogBody({ children, className }) {
    return _jsx("div", { className: cn('min-h-0 flex-1 overflow-y-auto px-5 py-4', className), children: children });
}
export function DialogFooter({ children, className }) {
    return (_jsx("div", { className: cn('dialog-footer flex shrink-0 items-center justify-end gap-2 border-t border-border bg-background/40 px-5 py-3', className), children: children }));
}
//# sourceMappingURL=dialog.js.map